/**
 * Footer: campaign labelling, legal links, and data-source attributions.
 *
 * Every identity value comes from content/identity.ts. § 30 zákona
 * č. 491/2001 Sb. requires campaign material to name the zadavatel and the
 * zpracovatel; hardcoding either here would let the two drift apart.
 */
import { Link } from 'react-router-dom';
import { ELECTION, IDENTITY } from '@content/identity';
import { openConsentSettings } from '@/lib/consent';

interface FooterProps {
  /** Data-source attributions collected from the layers currently offered. */
  attributions?: string[];
  /**
   * Compact variant for the map page.
   *
   * The map fills the viewport and captures the mouse wheel, so a tall footer
   * below the fold would be genuinely hard to reach on desktop — and this
   * footer carries the § 30 campaign labelling and the privacy link, which
   * must not be hard to reach. The compact form keeps the labelling and the
   * links always visible and folds only the source list away.
   */
  compact?: boolean;
}

export function Footer({ attributions = [], compact = false }: FooterProps) {
  // The basemap is always present, so its attribution is fixed. Everything
  // else comes from the layers the API actually offers — listing a source we
  // are not currently using would be its own small inaccuracy.
  const uniqueSources = [...new Set(['© OpenStreetMap contributors', 'Protomaps', ...attributions])];

  if (compact) {
    return (
      <footer className="border-t border-slate-200 bg-white px-4 py-2 text-xs text-slate-600">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="font-medium text-slate-900">
            Zadavatel: {IDENTITY.zadavatel} · Zpracovatel: {IDENTITY.zpracovatel}
          </span>
          {IDENTITY.coalitionPartners.length > 0 && (
            <span>Koaliční partneři: {IDENTITY.coalitionPartners.join(', ')}</span>
          )}
          <Link className="underline hover:no-underline" to="/soukromi">
            Ochrana soukromí
          </Link>
          <Link className="underline hover:no-underline" to="/pravidla">
            Pravidla pro podněty
          </Link>
          <button type="button" className="underline hover:no-underline" onClick={openConsentSettings}>
            Nastavení cookies
          </button>
          <details className="ml-auto">
            <summary className="cursor-pointer underline hover:no-underline">
              Zdroje dat a kontakt
            </summary>
            <div className="mt-1 space-y-0.5 py-1">
              <p>
                {IDENTITY.movement}, IČO {IDENTITY.ico}, {IDENTITY.address} · datová schránka{' '}
                {IDENTITY.dataBox} ·{' '}
                <a className="underline" href={`mailto:${IDENTITY.contact}`}>
                  {IDENTITY.contact}
                </a>{' '}
                · {IDENTITY.phone}
              </p>
              <p>{uniqueSources.join(' · ')}</p>
            </div>
          </details>
        </div>
      </footer>
    );
  }

  return (
    <footer className="border-t border-slate-200 bg-white px-4 py-6 text-sm text-slate-700">
      <div className="mx-auto grid max-w-5xl gap-6 sm:grid-cols-3">
        <div>
          <p className="font-semibold text-slate-900">{IDENTITY.list}</p>
          <p>{IDENTITY.movement}</p>
          <p className="mt-2 text-slate-600">
            {ELECTION.name}
            <br />
            {ELECTION.dates}
          </p>
        </div>

        <nav aria-label="Právní informace">
          <ul className="space-y-1">
            <li>
              <Link className="underline hover:no-underline" to="/soukromi">
                Ochrana soukromí
              </Link>
            </li>
            <li>
              <Link className="underline hover:no-underline" to="/pravidla">
                Pravidla pro podněty
              </Link>
            </li>
            <li>
              {/*
                Withdrawal must be as easy as granting, so this link is
                permanent and reopens the panel with the current choices —
                even while the consent layer is flagged off, in which case it
                explains that nothing beyond the necessary storage is used.
              */}
              <button
                type="button"
                className="underline hover:no-underline"
                onClick={openConsentSettings}
              >
                Nastavení cookies
              </button>
            </li>
            <li>
              <a className="underline hover:no-underline" href={ELECTION.mainSite}>
                {new URL(ELECTION.mainSite).host}
              </a>
            </li>
          </ul>
        </nav>

        <div className="text-slate-600">
          <p className="font-medium text-slate-900">Zdroje dat</p>
          <ul className="mt-1 space-y-0.5">
            {uniqueSources.map((source) => (
              <li key={source}>{source}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* § 30 zákona č. 491/2001 Sb. — campaign labelling. */}
      <div className="mx-auto mt-6 max-w-5xl border-t border-slate-200 pt-4 text-xs text-slate-600">
        <p>
          Zadavatel: {IDENTITY.zadavatel} · Zpracovatel: {IDENTITY.zpracovatel}
        </p>
        {IDENTITY.coalitionPartners.length > 0 && (
          <p>Koaliční partneři: {IDENTITY.coalitionPartners.join(', ')}</p>
        )}
        <p className="mt-1">
          {IDENTITY.movement}, IČO {IDENTITY.ico}, {IDENTITY.address} · datová schránka{' '}
          {IDENTITY.dataBox} ·{' '}
          <a className="underline" href={`mailto:${IDENTITY.contact}`}>
            {IDENTITY.contact}
          </a>{' '}
          · {IDENTITY.phone}
        </p>
      </div>
    </footer>
  );
}
